/* Khaybar motion runtime. Loaded deferred after gsap, ScrollTrigger and lenis.
   Rules: the CSS default of every scene is the finished composition; this file only adds start states and animates them.
   Reduced motion: no GSAP, no Lenis, no cursor; only the board panel, toggles, sticky bar and locks are wired. */
(() => {
  const html = document.documentElement;
  const body = document.body;
  const RM = matchMedia('(prefers-reduced-motion: reduce)');
  const FINE = matchMedia('(pointer: fine) and (min-width: 1000px)');
  const motionOn = body.dataset.kcMotion !== 'off' && !RM.matches && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const state = { lenis: null, cleanups: new Map(), ready: false };
  window.kc = state;

  /* ---------- locks (Lenis stop/start for dialogs) ---------- */
  let lockCount = 0;
  const lock = () => { lockCount += 1; html.classList.add('kc-locked'); state.lenis && state.lenis.stop(); };
  const unlock = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) { html.classList.remove('kc-locked'); state.lenis && state.lenis.start(); } };
  window.addEventListener('kc:lock', lock);
  window.addEventListener('kc:unlock', unlock);
  const watchClass = (el, cls) => {
    if (!el) return;
    let was = el.classList.contains(cls);
    new MutationObserver(() => {
      const is = el.classList.contains(cls);
      if (is && !was) lock();
      if (!is && was) unlock();
      was = is;
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  };

  /* ---------- board panel ---------- */
  function board() {
    const panel = $('[data-kc-board]');
    const scrim = $('[data-kc-board-scrim]');
    const openers = $$('[data-kc-board-open]');
    if (!panel || !openers.length) return;
    let lastFocus = null;
    const open = () => {
      lastFocus = document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      scrim && scrim.classList.add('is-open');
      openers.forEach((o) => o.setAttribute('aria-expanded', 'true'));
      lock();
      const first = panel.querySelector('[data-kc-board-close]');
      if (typeof window.trapFocus === 'function') window.trapFocus(panel, first); else first && first.focus();
    };
    const close = () => {
      if (!panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      scrim && scrim.classList.remove('is-open');
      openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));
      unlock();
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(lastFocus); else lastFocus && lastFocus.focus();
    };
    openers.forEach((o) => o.addEventListener('click', (e) => { e.preventDefault(); panel.classList.contains('is-open') ? close() : open(); }));
    $$('[data-kc-board-close]').forEach((b) => b.addEventListener('click', close));
    scrim && scrim.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    const preview = $('[data-kc-board-preview]');
    if (preview) {
      $$('[data-kc-row]', panel).forEach((row) => {
        row.addEventListener('mouseenter', () => {
          const src = row.dataset.kcPreviewBack || row.dataset.kcPreview;
          if (!src) return;
          if (preview.getAttribute('src') !== src) { preview.classList.remove('is-on'); preview.src = src; }
          preview.onload = () => preview.classList.add('is-on');
          if (preview.complete) preview.classList.add('is-on');
        });
      });
    }
  }

  /* ---------- header hide on scroll down, show on scroll up ---------- */
  function header() {
    const el = $('[data-kc-header]');
    if (!el || RM.matches) return;
    let last = window.scrollY, ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y > 120 && y - last > 8) el.classList.add('is-hidden');
        else if (last - y > 8 || y < 120) el.classList.remove('is-hidden');
        last = y; ticking = false;
      });
    }, { passive: true });
  }

  /* ---------- lead piece front/back toggle (mobile) ---------- */
  function toggles(root = document) {
    $$('[data-kc-toggle]', root).forEach((t) => {
      const scene = t.closest('[data-kc-scene]');
      // The toggle only exists once JS can drive it; the static markup ships it hidden and
      // shows both faces, so a no-JS visitor still sees the front and the back print.
      if (scene && !scene.dataset.face) scene.dataset.face = 'front';
      t.hidden = false;
      t.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-face]');
        if (!b || !scene) return;
        scene.dataset.face = b.dataset.face;
        $$('button[data-face]', t).forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
      });
    });
  }

  /* ---------- collection: in stock only ---------- */
  function stockToggle(root = document) {
    $$('[data-kc-instock-toggle]', root).forEach((btn) => {
      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const rows = btn.closest('[data-kc-scene], section, .shopify-section') || document;
        $$('.kc-row--soldout, .kc-row--archive, .kc-card--soldout, .kc-card--archive', rows).forEach((r) => { r.hidden = on; });
      });
    });
  }

  /* ---------- low stock labels on the size pills ---------- */
  function lowStock(root = document) {
    const data = $('[data-kc-low-stock]', root);
    if (!data) return;
    let map = {};
    try { map = JSON.parse(data.textContent || '{}'); } catch (e) { return; }
    $$('.product-form__input--pill label', root).forEach((label) => {
      const key = (label.textContent || '').trim();
      const n = map[key];
      if (!n || label.querySelector('.kc-low')) return;
      const em = document.createElement('em');
      em.className = 'kc-low';
      em.textContent = n === 1 ? 'last one' : n + ' left';
      label.appendChild(em);
    });
  }

  /* ---------- product page sticky bar ---------- */
  function stickyBar(root = document) {
    const bar = $('[data-kc-sticky]', root);
    if (!bar) return;
    const info = $('product-info');
    const form = info && info.querySelector('form[data-type="add-to-cart-form"]');
    const realBtn = () => (form && (form.querySelector('[type="submit"]') || form.querySelector('button'))) || null;
    const btn = realBtn();
    if (!btn) return;
    // The bar shows whenever the real button is off screen, above or below, so on a phone the
    // add to cart is one tap away from the first scroll. Conversion first.
    const io = new IntersectionObserver(([entry]) => {
      bar.hidden = entry.isIntersecting;
    }, { threshold: 0 });
    io.observe(btn);
    // Shopify's preview bar (draft themes only) covers the bottom edge; lift the bar above it.
    const lift = () => { const pb = document.getElementById('preview-bar-iframe'); bar.style.bottom = pb ? pb.getBoundingClientRect().height + 'px' : ''; };
    lift(); window.addEventListener('load', lift); setTimeout(lift, 1500);
    const add = $('[data-kc-sticky-add]', bar);
    add && add.addEventListener('click', () => { const b = realBtn(); if (b) { b.scrollIntoView({ block: 'center', behavior: 'smooth' }); setTimeout(() => b.click(), 350); } });
    const sizeLabel = $('[data-kc-sticky-size]', bar);
    const sync = () => {
      if (!sizeLabel || !info) return;
      const checked = info.querySelector('variant-selects input:checked');
      sizeLabel.textContent = checked ? (checked.value || '').trim() : '';
    };
    info && info.addEventListener('change', sync);
    sync();
  }

  /* ---------- smooth anchor links ---------- */
  function anchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - 64;
      if (state.lenis) state.lenis.scrollTo(top, { duration: 1.2 }); else window.scrollTo({ top, behavior: RM.matches ? 'auto' : 'smooth' });
    });
  }

  /* ---------- gate: preloader (CSS keyframes, class set inline in the head) and page transitions ---------- */
  function gate() {
    const g = $('[data-kc-gate]');
    if (!g) return;
    const finish = () => {
      html.classList.remove('kc-preload');
      try { sessionStorage.setItem('kc-seen', '1'); } catch (e) {}
      if (!state.ready) { state.ready = true; window.dispatchEvent(new Event('kc:ready')); }
    };
    if (html.classList.contains('kc-preload')) {
      const leaf = g.querySelector('.kc-gate__leaf--l');
      leaf.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 2400);
    } else {
      finish();
    }
    if (RM.matches) return;
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('data-no-transition') || a.hasAttribute('data-kc-board-open') || a.closest('form')) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return;
      if (/^\/(cart|checkout|account|admin|customer_authentication|\d+\/checkouts)/.test(url.pathname)) return;
      if (/^(mailto|tel):/.test(a.getAttribute('href'))) return;
      e.preventDefault();
      g.classList.add('is-closed');
      setTimeout(() => { location.href = url.href; }, 560);
    }, true);
    window.addEventListener('pageshow', (e) => { if (e.persisted) g.classList.remove('is-closed'); });
  }

  /* ---------- cursor ---------- */
  function cursor() {
    const c = $('.kc-cursor');
    if (!c || body.dataset.kcCursor === 'off') return;
    const label = c.querySelector('.kc-cursor__label');
    const x = gsap.quickTo(c, 'x', { duration: 0.18, ease: 'power3' });
    const y = gsap.quickTo(c, 'y', { duration: 0.18, ease: 'power3' });
    window.addEventListener('mousemove', (e) => { x(e.clientX); y(e.clientY); }, { passive: true });
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest('[data-cursor]');
      if (t) { c.classList.add('is-active'); label.textContent = t.dataset.cursor; } else { c.classList.remove('is-active'); }
    });
    document.addEventListener('mousedown', () => c.classList.add('is-down'));
    document.addEventListener('mouseup', () => c.classList.remove('is-down'));
    document.addEventListener('mouseleave', () => (c.style.opacity = '0'));
    document.addEventListener('mouseenter', () => (c.style.opacity = ''));
  }

  /* ---------- generic line rises ---------- */
  function lines(root = document) {
    $$('[data-kc-lines]', root).forEach((el) => {
      const spans = $$('.kc-line > span', el);
      if (!spans.length) return;
      gsap.from(spans, { yPercent: 110, duration: 1.1, ease: 'expo.out', stagger: 0.05, scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
    });
    $$('[data-kc-rise]', root).forEach((el) => {
      gsap.from(el, { y: 32, opacity: 0, duration: 1, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 92%', once: true } });
    });
  }

  /* ---------- scene registry ---------- */
  const scenes = {
    'hero-gate'(root) {
      const l = $('.kc-hero__half--l', root), r = $('.kc-hero__half--r', root), media = $('.kc-hero__media', root);
      if (!l || !r) return;
      if (FINE.matches) {
        gsap.set([l, r], { x: 0 });
        media && gsap.set(media, { scale: 1.08 });
        const tl = gsap.timeline({ scrollTrigger: { trigger: root, start: 'top top', end: '+=100%', scrub: 0.6 } });
        tl.to(l, { x: '-18vw', ease: 'none' }, 0).to(r, { x: '18vw', ease: 'none' }, 0);
        media && tl.to(media, { scale: 1, ease: 'none' }, 0);
        const meta = $$('[data-kc-hero-fade]', root);
        meta.length && tl.to(meta, { opacity: 0, y: -24, ease: 'none' }, 0.2);
      } else {
        gsap.set([l, r], { x: 0 });
        const play = () => { gsap.to(l, { x: '-2.5vw', duration: 1.4, ease: 'expo.inOut' }); gsap.to(r, { x: '2.5vw', duration: 1.4, ease: 'expo.inOut' }); };
        state.ready ? play() : window.addEventListener('kc:ready', play, { once: true });
      }
    },
    'hero-campaign'(root) {
      const media = $('.kc-camp__media', root);
      if (!media) return;
      gsap.fromTo(media, { scale: 1.08 }, { scale: 1, duration: 1.8, ease: 'expo.out' });
      if (!FINE.matches) return;
      gsap.to(media, { yPercent: 14, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: true } });
    },
    band(root) {
      const media = $('.kc-band__media', root);
      if (!media || !FINE.matches) return;
      gsap.fromTo(media, { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true } });
    },
    grid(root) {
      const cards = $$('.kc-card', root);
      if (!cards.length) return;
      gsap.from(cards, { y: 36, opacity: 0, duration: 0.9, ease: 'expo.out', stagger: 0.06, scrollTrigger: { trigger: root, start: 'top 82%', once: true } });
    },
    feature(root) {
      const frames = $$('.kc-feat__frame', root);
      if (!frames.length) return;
      gsap.from(frames, { y: 40, opacity: 0, duration: 1, ease: 'expo.out', stagger: 0.1, scrollTrigger: { trigger: root, start: 'top 80%', once: true } });
    },
    'lead-piece'(root) {
      if (!FINE.matches) return;
      const back = $('.kc-lead__img--back', root), front = $('.kc-lead__img--front', root);
      if (!back || !front) return;
      root.classList.add('is-pinned');
      // A dissolve, not a clip. The two frames are different crops of different garments,
      // so a hard seam between them read as a broken collage at every midpoint.
      const st = { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 0.5 };
      gsap.fromTo(front, { opacity: 1 }, { opacity: 0, ease: 'none', scrollTrigger: st });
      gsap.fromTo(back, { scale: 1.1 }, { scale: 1, ease: 'none', scrollTrigger: st });
    },
    'the-back'(root) {
      const title = $('.kc-back__title', root), img = $('.kc-back__img', root), leaf = $('.kc-back__leaf', root);
      const seam = $('.kc-back__seam', root), rule = $('.kc-back__rule', root), reading = $('.kc-back__reading', root);
      root.classList.add('is-pinned');
      const tl = gsap.timeline({ scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 0.5 } });
      seam && tl.fromTo(seam, { scaleY: 0 }, { scaleY: 1, ease: 'none', duration: 0.18 }, 0);
      leaf && tl.fromTo(leaf, { xPercent: 0 }, { xPercent: 100, ease: 'none', duration: 0.55 }, 0.08);
      img && tl.fromTo(img, { scale: 1.14 }, { scale: 1, ease: 'none', duration: 0.95 }, 0);
      title && tl.fromTo(title, { '--wdth': 74 }, { '--wdth': 98, ease: 'none', duration: 0.5 }, 0);
      reading && tl.from(reading, { y: 28, opacity: 0, ease: 'none', duration: 0.18 }, 0.1);
      rule && tl.fromTo(rule, { scaleX: 0 }, { scaleX: 1, ease: 'none', duration: 0.18 }, 0.3);
    },
    rack(root) {
      // Native swipe strip; the index counter is wired in wireAlways so it works with motion off.
    },
    'seventy-two'(root) {
      const tally = $('.kc-tally', root), strokes = $$('.kc-tally i', root), count = $('[data-kc-count]', root);
      if (!tally || !strokes.length) return;
      root.classList.add('is-pinned');
      tally.classList.add('is-armed');
      strokes.forEach((s) => s.classList.remove('is-lit'));
      ScrollTrigger.create({
        trigger: root, start: 'top top', end: 'bottom bottom', scrub: true,
        onUpdate: (self) => {
          const n = Math.round(self.progress * strokes.length);
          strokes.forEach((s, i) => s.classList.toggle('is-lit', i < n));
          if (count) count.textContent = String(n).padStart(2, '0');
        },
      });
    },
    'next-drop'(root) {
      const base = parseFloat(getComputedStyle(root).getPropertyValue('--gap')) || 24;
      gsap.fromTo(root, { '--gap': base + '%' }, { '--gap': base + 10 + '%', ease: 'none', scrollTrigger: { trigger: root, start: 'top 80%', end: 'bottom 20%', scrub: 0.6 } });
    },
    'footer-gate'(root) {
      const l = $('[data-kc-half="l"]', root), r = $('[data-kc-half="r"]', root), plate = $('.kc-footer__plate', root);
      if (!l || !r || !FINE.matches) return;
      gsap.set(l, { x: '-14vw' }); gsap.set(r, { x: '14vw' });
      const tl = gsap.timeline({ scrollTrigger: { trigger: plate || root, start: 'top bottom', end: 'top 15%', scrub: 0.6 } });
      tl.to(l, { x: 0, ease: 'none' }, 0).to(r, { x: 0, ease: 'none' }, 0);
    },
    'story-chapters'(root) {
      $$('.kc-chapter', root).forEach((ch) => {
        const leaves = $$('.kc-chapter__leaf', ch);
        if (!leaves.length) return;
        gsap.fromTo(leaves, { xPercent: (i) => (i === 0 ? 0 : 0) }, { xPercent: (i) => (i === 0 ? -100 : 100), ease: 'none', scrollTrigger: { trigger: ch, start: 'top 90%', end: 'top 25%', scrub: 0.6 } });
      });
    },
  };

  function mount(root = document) {
    $$('[data-kc-scene]', root).forEach((el) => {
      const name = el.dataset.kcScene;
      if (!scenes[name] || state.cleanups.has(el)) return;
      const cleanup = scenes[name](el);
      state.cleanups.set(el, typeof cleanup === 'function' ? cleanup : () => {});
    });
    lines(root);
  }
  function unmount(root) {
    $$('[data-kc-scene]', root).concat(root.matches && root.matches('[data-kc-scene]') ? [root] : []).forEach((el) => {
      const fn = state.cleanups.get(el);
      if (fn) { fn(); state.cleanups.delete(el); }
    });
    ScrollTrigger.getAll().forEach((t) => { if (root.contains(t.trigger)) t.kill(); });
  }

  /* ---------- boot ---------- */
  /* ---------- rack index counter: a native scroll strip, so it must work with motion off ---------- */
  function rackIndex(root = document) {
    $$('[data-kc-scene="rack"]', root).forEach((rack) => {
      const stage = $('.kc-rack__stage', rack), index = $('[data-kc-index]', rack), frames = $$('.kc-rack__frame', rack);
      if (!stage || !index || !frames.length || stage.dataset.kcIndexed) return;
      stage.dataset.kcIndexed = '1';
      stage.addEventListener('scroll', () => {
        const w = frames[0].getBoundingClientRect().width + 24;
        index.textContent = String(Math.min(frames.length, Math.round(stage.scrollLeft / w) + 1)).padStart(2, '0');
      }, { passive: true });
    });
  }

  function wireAlways(root = document) {
    toggles(root); stockToggle(root); stickyBar(root); lowStock(root); rackIndex(root);
  }
  // Dawn re-renders the variant picker on every variant change, so re-apply the low stock labels.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try { subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => lowStock(document), 0)); } catch (e) {}
  }
  board(); header(); anchors();
  watchClass($('cart-drawer'), 'active');
  watchClass($('[data-kc-board]'), 'is-open');
  $$('[data-kc-sg-root]').forEach((el) => watchClass(el, 'is-open'));
  wireAlways();

  if (!motionOn) {
    html.classList.add('kc-rm');
    html.classList.remove('kc-preload');
    state.ready = true;
    window.dispatchEvent(new Event('kc:ready'));
    document.addEventListener('shopify:section:load', (e) => wireAlways(e.target));
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  html.classList.add('is-motion');
  if (FINE.matches && typeof Lenis !== 'undefined') {
    state.lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1 });
    state.lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => state.lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    html.classList.add('kc-fine');
    cursor();
  }
  gate();
  mount();
  window.addEventListener('load', () => ScrollTrigger.refresh());
  document.addEventListener('shopify:section:load', (e) => { wireAlways(e.target); mount(e.target); ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));
})();
